import { useCallback, useMemo } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { useAiUpsell } from '../components/ai/aiUpsellContext';

export const AI_MODULE = 'AI_ASSISTANT';

/**
 * Whether the clinic's plan includes ClinVea AI (the AI_ASSISTANT module), and helpers that turn an AI click
 * into the "not in your plan" upsell when it doesn't (medopd-docs 126).
 *
 * - `aiEnabled`: true when the module is on, for super_admin (never upsold), and while permissions are still
 *   loading (unknown counts as on, like useAiFeatureFlags — the server enforces the module either way).
 * - `aiModuleOff`: true only once permissions have loaded and the module is definitely off. Use it to skip
 *   background AI calls silently and to show the AI badge.
 * - `requireAi(label)`: true when AI may run; otherwise opens the upsell and returns false.
 * - `guardAi(fn, label)`: wraps a click handler so it runs only when AI is enabled.
 *
 * This replaces ONLY the module check. Role/permission checks (doctors-only clinical AI, `can(...)`) stay
 * where they are: a user who may not use a feature still doesn't see its button.
 */
export function useAiModule() {
  const { moduleEnabled, isSuperAdmin, isLoading, role } = usePermissions();
  const upsell = useAiUpsell();
  // No module information at all (e.g. a partial context in an isolated test) counts as unknown, i.e. on.
  const aiModuleOff = typeof moduleEnabled === 'function'
    && !isLoading && !isSuperAdmin && role !== 'super_admin' && !moduleEnabled(AI_MODULE);
  const aiEnabled = !aiModuleOff;

  const requireAi = useCallback((featureLabel?: string): boolean => {
    if (!aiModuleOff) return true;
    upsell?.openUpsell(featureLabel);
    return false;
  }, [aiModuleOff, upsell]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- a click handler of any shape
  const guardAi = useCallback(<T extends (...a: any[]) => unknown>(fn: T, featureLabel?: string): T => {
    return ((...args: Parameters<T>) => (requireAi(featureLabel) ? fn(...args) : undefined)) as T;
  }, [requireAi]);

  return useMemo(() => ({ aiEnabled, aiModuleOff, requireAi, guardAi }), [aiEnabled, aiModuleOff, requireAi, guardAi]);
}
